import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface PitchDeckModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PitchDeckModal = ({ open, onOpenChange }: PitchDeckModalProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      src: '/lovable-uploads/a77dbed7-d05e-40a5-9704-e7d8b0fab45f.png',
      title: 'iSPAI - Integrated Special Purpose AI',
      description: 'The Wisdom of the Workforce'
    },
    {
      src: '/lovable-uploads/96ee16c3-6ac2-4227-9ae2-f748c71902c9.png',
      title: 'The Pain',
      description: 'AI is fundamentally changing the role of the workforce inside the enterprise'
    },
    {
      src: '/lovable-uploads/b2ff9fd7-55fc-4e9d-ad58-5327f7b6a6a5.png',
      title: 'The Problem',
      description: 'AI\'s knowledge is too general to properly support the enterprise'
    },
    {
      src: '/lovable-uploads/c0fedb42-3f6e-41e6-b99a-952cd6381090.png',
      title: 'The Purpose',
      description: 'Democratize knowledge and define a role for humans in an AI-driven future'
    },
    {
      src: '/lovable-uploads/6a02ae56-7ab6-453f-8970-60920ed016bb.png',
      title: 'The Solution',
      description: 'The world\'s first AI-enabled knowledge network and marketplace for the wisdom of the workforce'
    },
    {
      src: '/lovable-uploads/2f97cf4d-41b6-495f-bad3-69ef8321b68e.png',
      title: 'The Roadmap',
      description: 'We have a well-defined path forward to iterate PMF with customers'
    },
    {
      src: '/lovable-uploads/b0c974a5-2c04-473d-86ae-5e8e42745b22.png',
      title: 'Revenue',
      description: 'We build upon proven commercial models including monthly subscriptions, transaction fees, and professional services'
    },
    {
      src: '/lovable-uploads/33e54ca0-41f4-47f6-969f-824f526115ba.png',
      title: 'Key Leaders',
      description: 'Specifically selected to execute upon the mission of iSPAI'
    },
    {
      src: '/lovable-uploads/378088cc-6db6-4a5c-826d-3c1295b7e123.png',
      title: 'The Pre-Seed Raise',
      description: 'We are raising for our next milestone using a SAFE note at a $10 million pre-money cap'
    },
    {
      src: '/lovable-uploads/76be0ca3-1b4d-4218-98dc-78ccd060e245.png',
      title: 'Market Size',
      description: 'We estimate iSPAI\'s SOM to be 1 to 5% of SAM across the IP and KM markets'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight') nextSlide();
    if (e.key === 'ArrowLeft') prevSlide();
    if (e.key === 'Escape') onOpenChange(false);
  };

  React.useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open]);

  React.useEffect(() => {
    if (open) {
      setCurrentSlide(0);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-full h-[90vh] p-0 border-0 bg-transparent">
        <div className="relative w-full h-full bg-black/95 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
            <div className="flex justify-between items-center text-white">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-semibold">iSPAI Pitch Deck</h3>
                <span className="text-sm text-gray-300">
                  {currentSlide + 1} of {slides.length}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onOpenChange(false)}
                className="text-white hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Main Content */}
          <div className="relative w-full h-full flex items-center justify-center p-8 pt-16">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="relative max-w-full max-h-full"
              >
                <img
                  src={slides[currentSlide].src}
                  alt={slides[currentSlide].title}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                />
              </motion.div>
            </AnimatePresence>

            {/* Navigation Buttons */}
            <Button
              variant="ghost"
              size="sm"
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12 rounded-full"
              disabled={slides.length <= 1}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12 rounded-full"
              disabled={slides.length <= 1}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>

          {/* Slide Indicators */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/50 rounded-full px-4 py-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide
                    ? 'bg-white scale-125'
                    : 'bg-white/40 hover:bg-white/60'
                }`}
              />
            ))}
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PitchDeckModal;